import { Button } from '@ctw/shared/components/button';
import { PatientSummarizationBanner } from '@ctw/shared/content/summarization/patient-summarization-banner';
import { useUserPatientSummarizationContext } from '@ctw/shared/content/summarization/user-patient-summarization-context';
import { useUiFlag } from '@ctw/shared/hooks/use-ui-preference';
import { formatDateToLocalHumanDateString, numberOfDaysBetween } from '@ctw/shared/utils/dates';
import { tw } from '@ctw/shared/utils/tailwind';
import {
  faArrowTrendUp,
  faCircleExclamation,
  faSparkles,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { addDays, endOfMonth } from 'date-fns';
import pluralize from 'pluralize';
import { useCallback, useMemo } from 'react';
import {
  getUiFlagLimitApproachingBannerHidden,
  getUiFlagLimitReachedBannerHidden,
  usePatientSummarizationUpgradeModal,
} from './patient-summarization-status-card';

export interface SummarizationLimitBannerProps {
  variant?: 'inline' | 'standalone';
  dismissable?: boolean;
  summaryJobId?: string;
  summaryJobStatus?: string;
}

export const PatientSummarizationLimitApproachingBanner = ({
  variant = 'inline',
  dismissable = true,
  summaryJobId,
  summaryJobStatus,
}: SummarizationLimitBannerProps) => {
  const { summariesAllotted, summariesUsed, showMetering } = useUserPatientSummarizationContext();
  const { openUpgradeModal } = usePatientSummarizationUpgradeModal();
  const flagKey = useMemo(() => getUiFlagLimitApproachingBannerHidden(), []);
  const [flagHidden, setFlagHidden] = useUiFlag(flagKey, false);
  const hidden = dismissable && flagHidden;

  const hideBanner = useCallback(() => {
    setFlagHidden(true);
  }, [setFlagHidden]);

  const numberOfDaysUntilReset = numberOfDaysBetween({
    start: new Date(),
    end: addDays(endOfMonth(new Date()), 1),
  });

  if (!showMetering) {
    return null;
  }

  const body = (
    <>
      {summariesUsed.toLocaleString('en-US')} of {summariesAllotted.toLocaleString('en-US')} free
      summaries have been used this month. Resets in{' '}
      {pluralize('day', numberOfDaysUntilReset, true)}.
    </>
  );

  const action =
    variant === 'standalone' ? (
      <Button
        type="button"
        variant="primary"
        onClick={openUpgradeModal}
        className={tw`bg-caution-main text-white hover:bg-caution-main/90`}
        telemetryInteractionName="patient_summary_upgrade_modal_opened"
        telemetryObfuscatedIdentifier="Learn More"
        telemetryContext={{
          summaryJobId,
          summaryJobStatus,
          upgradeModalTrigger: 'learn-more-link',
        }}
      >
        <div className={tw`flex items-center space-x-2`}>
          <FontAwesomeIcon icon={faArrowTrendUp} className={tw`h-4 w-4`} />
          <div className={tw`whitespace-nowrap text-sm`}>Learn More</div>
        </div>
      </Button>
    ) : (
      <Button
        type="button"
        variant="link"
        onClick={openUpgradeModal}
        className={tw`whitespace-nowrap font-medium text-caution-text underline`}
        telemetryInteractionName="patient_summary_upgrade_modal_opened"
        telemetryObfuscatedIdentifier="Learn More"
        telemetryContext={{
          summaryJobId,
          summaryJobStatus,
          upgradeModalTrigger: 'learn-more-link',
        }}
      >
        Learn More
      </Button>
    );

  return (
    <PatientSummarizationBanner
      tone="caution"
      icon={faCircleExclamation}
      title="You're approaching your monthly AI summary limit"
      body={body}
      action={action}
      dismissable={dismissable}
      isDismissed={hidden}
      onDismiss={hideBanner}
      variant={variant}
    />
  );
};

export const PatientSummarizationLimitReachedBanner = ({
  variant = 'inline',
  dismissable = true,
  summaryJobId,
  summaryJobStatus,
}: SummarizationLimitBannerProps) => {
  const { summariesAllotted, showMetering } = useUserPatientSummarizationContext();
  const { openUpgradeModal } = usePatientSummarizationUpgradeModal();
  const flagKey = useMemo(() => getUiFlagLimitReachedBannerHidden(), []);
  const [flagHidden, setFlagHidden] = useUiFlag(flagKey, false);
  const hidden = dismissable && flagHidden;

  const hideBanner = useCallback(() => {
    setFlagHidden(true);
  }, [setFlagHidden]);

  const formattedSummaryLimitResetDate = formatDateToLocalHumanDateString(
    addDays(endOfMonth(new Date()), 1),
  );

  if (!showMetering) {
    return null;
  }

  const body = (
    <>
      You've used all {summariesAllotted.toLocaleString('en-US')} free summaries this month. Upgrade
      for unlimited access or wait until your quota resets ({formattedSummaryLimitResetDate}).
    </>
  );

  const action =
    variant === 'standalone' ? (
      <Button
        type="button"
        variant="primary-destructive"
        onClick={openUpgradeModal}
        telemetryInteractionName="patient_summary_upgrade_modal_opened"
        telemetryObfuscatedIdentifier="Upgrade Now"
        telemetryContext={{
          summaryJobId,
          summaryJobStatus,
          upgradeModalTrigger: 'upgrade-now-button',
        }}
      >
        <div className={tw`flex items-center space-x-2`}>
          <FontAwesomeIcon icon={faArrowTrendUp} className={tw`h-4 w-4`} />
          <div className={tw`whitespace-nowrap text-sm`}>Upgrade Now</div>
        </div>
      </Button>
    ) : undefined;

  return (
    <PatientSummarizationBanner
      tone="error"
      icon={faCircleExclamation}
      title={variant === 'standalone' ? "You've reached your monthly AI summary limit" : undefined}
      body={body}
      action={action}
      dismissable={dismissable}
      isDismissed={hidden}
      onDismiss={hideBanner}
      variant={variant}
    />
  );
};

export const PatientSummarizationInsufficientDataBanner = () => {
  return (
    <PatientSummarizationBanner
      tone="caution"
      icon={faSparkles}
      title="Patient data hasn't finished processing"
      body="The patient history job may still be running — check back shortly."
      variant="standalone"
      background="white"
    />
  );
};
